import UserService from "api/user/user";
import moment from "moment";
import {useCallback, useEffect, useMemo, useState} from 'react';
import {STATS_QUOTE_SUMMARY} from "v4/data/apiRoutes";
import useFetch from "v4/hooks/useFetch";
import {generateUrl} from "v4/services/api.service";
import useToggle from "v4/hooks/useToggle";

export default function useDashboardQuotesSummary(isInView) {
    const [{data, isLoading, isError}, fetchQuotesSummary] = useFetch();
    const [refresh, toggleRefresh] = useToggle();
    const [dateInterval, setDateInterval] = useState({
        beginAt: moment().startOf('month').format('YYYY-MM-DD'),
        endAt: moment().endOf('month').add(5, 'months').format('YYYY-MM-DD'),
    });

    const results = useMemo(() => {
        if (!data) return null;
        // put the "- Utilisateur désactivé" at the end of the list
        const resultsCopy = window.structuredClone(data.results);
        const disabledUser = resultsCopy.find(result => result.fullname === '- Utilisateur désactivé');
        if (disabledUser) {
            resultsCopy.splice(resultsCopy.indexOf(disabledUser), 1);
            resultsCopy.push(disabledUser);
        }

        return resultsCopy;
    }, [data]);

    useEffect(() => {
        if (!isInView) return;

        fetchQuotesSummary({
            url: generateUrl(STATS_QUOTE_SUMMARY, {...dateInterval}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateInterval, isInView, refresh]);


    const totalMonthKeys = Object.keys(results?.[0] ?? {}).filter(key => key.startsWith('total_year_'));
    const totalNbMonthKeys = Object.keys(results?.[0] ?? {}).filter(key => key.startsWith('nb_year_'));
    const totalWeightedMonthKeys = Object.keys(results?.[0] ?? {}).filter(key => key.startsWith('total_weighted_year_'));

    const [
        total_issued,
        nb_issued,
        total_won,
        nb_won,
        total_abandonned,
        nb_abandonned,
        total_lost,
        nb_lost,
        total_in_progress,
        nb_in_progress,
        total_late,
        nb_late,
        total_weighted_abandonned,
        total_weighted_late,
        total_weighted_lost,
        total_weighted_won,
        total_weighted_issued,
        total_weighted_in_progress,
        ...remainingMonths
    ] = results?.reduce((acc, row) => {
        const {
            total_issued,
            nb_issued,
            total_won,
            nb_won,
            total_abandonned,
            nb_abandonned,
            total_lost,
            nb_lost,
            total_in_progress,
            nb_in_progress,
            total_late,
            nb_late,
            total_weighted_abandonned,
            total_weighted_late,
            total_weighted_lost,
            total_weighted_won,
            total_weighted_issued,
            total_weighted_in_progress,
        } = row;

        return [
            parseFloat(acc[0]) + parseFloat((total_issued ?? '0')),
            parseFloat(acc[1]) + parseFloat((nb_issued ?? '0')),
            parseFloat(acc[2]) + parseFloat((total_won ?? '0')),
            parseFloat(acc[3]) + parseFloat((nb_won ?? '0')),
            parseFloat(acc[4]) + parseFloat((total_abandonned ?? '0')),
            parseFloat(acc[5]) + parseFloat((nb_abandonned ?? '0')),
            parseFloat(acc[6]) + parseFloat((total_lost ?? '0')),
            parseFloat(acc[7]) + parseFloat((nb_lost ?? '0')),
            parseFloat(acc[8]) + parseFloat((total_in_progress ?? '0')),
            parseFloat(acc[9]) + parseFloat((nb_in_progress ?? '0')),
            parseFloat(acc[10]) + parseFloat((total_late ?? '0')),
            parseFloat(acc[11]) + parseFloat((nb_late ?? '0')),
            parseFloat(acc[12]) + parseFloat((total_weighted_abandonned ?? '0')),
            parseFloat(acc[13]) + parseFloat((total_weighted_late ?? '0')),
            parseFloat(acc[14]) + parseFloat((total_weighted_lost ?? '0')),
            parseFloat(acc[15]) + parseFloat((total_weighted_won ?? '0')),
            parseFloat(acc[16]) + parseFloat((total_weighted_issued ?? '0')),
            parseFloat(acc[17]) + parseFloat((total_weighted_in_progress ?? '0')),
            ...totalMonthKeys.map((key, index) => {
                return parseFloat(acc[18 + index]) + parseFloat((row[key] ?? '0'));
            }),
            ...totalNbMonthKeys.map((key, index) => {
                return parseFloat(acc[18 + totalMonthKeys.length + index]) + parseFloat((row[key] ?? '0'));
            }),
            ...totalWeightedMonthKeys.map((key, index) => {
                return parseFloat(acc[18 + totalMonthKeys.length + totalNbMonthKeys.length + index]) + parseFloat((row[key] ?? '0'));
            }),
        ];
    }, Array(totalMonthKeys.length + totalNbMonthKeys.length + totalWeightedMonthKeys.length + 12).fill(0)) ?? [];

    const tableInfos = {
        remaining_months: Object.keys(results?.[0] ?? {})
                .filter(key => key.startsWith('nb_year_'))
                .map(key => {
                    const [yearNumber, monthNumber] = key
                        .replace('nb_year_', '')
                        .replace('month_', '')
                        .split('_');
                    const baseDate = moment(dateInterval.beginAt, 'YYYY-MM-DD');
                    const monthIndex = parseInt(monthNumber, 10) - 1;
                    const date = baseDate.clone().month(monthIndex).date(1);
                    const monthString = new Intl.DateTimeFormat(UserService.getLocale(), {month: 'long'}).format(date.toDate());

                    return {
                        yearNumber: parseInt(yearNumber, 10),
                        monthNumber: parseInt(monthNumber, 10),
                        label: monthString.slice(0, 1).toUpperCase() + monthString.slice(1)
                    };
                }),
        totalByColumn: {
            total_issued,
            nb_issued,
            total_won,
            nb_won,
            total_abandonned,
            nb_abandonned,
            total_lost,
            nb_lost,
            total_in_progress,
            nb_in_progress,
            total_late,
            nb_late,
            total_weighted_abandonned,
            total_weighted_late,
            total_weighted_lost,
            total_weighted_won,
            total_weighted_issued,
            total_weighted_in_progress,
            ...remainingMonths.reduce((acc, value, index) => {
                const key = index < totalMonthKeys.length ? totalMonthKeys[index] : index < totalMonthKeys.length + totalNbMonthKeys.length ? totalNbMonthKeys[index - totalMonthKeys.length] : totalWeightedMonthKeys[index - totalMonthKeys.length - totalNbMonthKeys.length];

                return {
                    ...acc,
                    [key]: value
                };
            }, {}),
        },
        currentYear: moment().year(),
        currentMonth: moment().month() + 1,
        canPreviousMonth: moment(dateInterval.beginAt).isAfter(moment().subtract(12, 'months')),
        canNextMonth: moment(dateInterval.endAt).isBefore(moment().add(12, 'months')),
    };

    const getPreSearch = useCallback((preSearchType, index) => {
        const {metadata} = data ?? {};
        const preSearch = getQuotePreSearch(preSearchType, metadata?.quoteStates);

        if (results?.[index]?.managedBy) {
            preSearch['managedBy'] = results[index].managedBy.split(',') ?? [];
        } else if (preSearchType.startsWith('total_')) {
            // all managedBy
            preSearch['managedBy'] = results?.map(({managedBy}) => managedBy.split(',')).flat();
        }

        return preSearch;
    }, [data, results]);

    const previousMonth = useCallback(() => {
        setDateInterval({
            beginAt: moment(dateInterval.beginAt).subtract(1, 'month').format('YYYY-MM-DD'),
            endAt: moment(dateInterval.endAt).subtract(1, 'month').format('YYYY-MM-DD'),
        });
    }, [dateInterval]);

    const nextMonth = useCallback(() => {
        setDateInterval({
            beginAt: moment(dateInterval.beginAt).add(1, 'month').format('YYYY-MM-DD'),
            endAt: moment(dateInterval.endAt).add(1, 'month').format('YYYY-MM-DD'),
        });
    }, [dateInterval]);

    const resetDateInterval = useCallback(() => {
        setDateInterval({
            beginAt: moment().startOf('month').format('YYYY-MM-DD'),
            endAt: moment().endOf('month').add(5, 'months').format('YYYY-MM-DD'),
        });
    }, []);

    return {
        data: results,
        tableInfos,
        getPreSearch,
        previousMonth,
        nextMonth,
        resetDateInterval,
        state: {
            isLoading,
            isError,
        },
        toggleRefresh,
    };
}

function getQuotePreSearch(preSearchType, quotesStatus = {}) {
    const preSearch = {};

    if (preSearchType.includes('issued')) {
        preSearch['status'] = [];
        preSearch['issuedAt'] = {
            equal: ">",
            begin: moment().startOf('year').format('DD-MM-YYYY'),
            end: ""
        };
    } else if (preSearchType.includes('won')) {
        preSearch['status'] = quotesStatus.isWon ?? [];
        preSearch['expectedSignedAt'] = {
            equal: ">",
            begin: moment().startOf('year').format('DD-MM-YYYY'),
            end: ""
        };
    } else if (preSearchType.includes('abandoned')) {
        preSearch['status'] = quotesStatus.isAbandoned ?? [];
        preSearch['issuedAt'] = {
            equal: ">",
            begin: moment().startOf('year').format('DD-MM-YYYY'),
            end: ""
        };
    } else if (preSearchType.includes('lost')) {
        preSearch['status'] = quotesStatus.isLost ?? [];
        preSearch['issuedAt'] = {
            equal: ">",
            begin: moment().startOf('year').format('DD-MM-YYYY'),
            end: ""
        };
    } else if (preSearchType.includes('in_progress')) {
        preSearch['status'] = quotesStatus.inProgress ?? [];
    } else if (preSearchType.includes('late')) {
        preSearch['status'] = quotesStatus.inProgress ?? [];
        preSearch['expectedSignedAt'] = {
            equal: "<",
            begin: "",
            end: moment().format('DD-MM-YYYY')
        };
    } else if (preSearchType.includes('month')) {
        const monthNumber = preSearchType.split('_').pop();
        preSearch['status'] = quotesStatus.inProgress ?? [];
        preSearch['expectedSignedAt'] = {
            equal: "><",
            begin: moment().month(monthNumber - 1).startOf('month').format('DD-MM-YYYY'),
            end: moment().month(monthNumber - 1).endOf('month').format('DD-MM-YYYY')
        };
    }

    return preSearch;
}
